import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight, Images } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { rooms } from '../mock';
import useReveal from '../hooks/useReveal';
import { Dialog, DialogContent } from './ui/dialog';

function RoomCard({ room, index, onOpen }) {
  const { lang, t } = useLang();
  const [ref, visible] = useReveal();
  const reversed = index % 2 === 1;

  return (
    <div
      ref={ref}
      className={`reveal ${visible ? 'is-visible' : ''} grid md:grid-cols-2 gap-8 md:gap-14 items-center`}
    >
      {/* Image */}
      <div className={`relative group cursor-pointer ${reversed ? 'md:order-2' : ''}`} onClick={() => onOpen(room, 0)}>
        <div className="overflow-hidden rounded-[2px] shadow-[0_25px_55px_-25px_rgba(42,36,32,0.5)]">
          <img
            src={room.images[0]}
            alt={room.name[lang]}
            className="w-full h-[320px] md:h-[460px] object-cover group-hover:scale-105 transition-transform duration-[1.1s] ease-out"
          />
        </div>
        <div className="absolute inset-0 bg-[#2A2420]/0 group-hover:bg-[#2A2420]/25 transition-colors duration-500 flex items-center justify-center">
          <span className="opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500 flex items-center gap-2 text-white font-body text-sm tracking-wide bg-[#B5603A]/90 px-5 py-2.5 rounded-full">
            <Images className="w-4 h-4" /> {t('rooms.view')}
          </span>
        </div>
        <span
          className="absolute -top-4 left-6 font-display text-[5rem] md:text-[7rem] leading-none opacity-20 select-none"
          style={{ color: room.accent }}
        >
          0{index + 1}
        </span>
      </div>

      {/* Text */}
      <div className={reversed ? 'md:order-1' : ''}>
        <span className="inline-block w-12 h-1 rounded-full mb-6" style={{ background: room.accent }} />
        <p className="font-body text-xs tracking-[0.3em] uppercase mb-3" style={{ color: room.accent }}>
          {room.subtitle[lang]}
        </p>
        <h3 className="font-display text-3xl md:text-5xl text-[#2A2420] mb-5">{room.name[lang]}</h3>
        <p className="font-body text-[#6E655B] text-lg leading-relaxed mb-7">{room.desc[lang]}</p>

        <div className="flex flex-wrap gap-2.5 mb-8">
          {t('amenities').slice(0, 4).map((a, i) => (
            <span key={i} className="font-body text-[13px] text-[#6E655B] bg-[#EFE5D4] px-4 py-1.5 rounded-full">
              {a}
            </span>
          ))}
        </div>

        <div className="flex gap-3">
          {room.images.map((img, i) => (
            <button
              key={i}
              onClick={() => onOpen(room, i)}
              className="w-16 h-16 md:w-20 md:h-20 overflow-hidden rounded-[2px] ring-1 ring-[#E7DAC4] hover:ring-2 transition-all"
              style={{ '--tw-ring-color': room.accent }}
            >
              <img src={img} alt="" className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Rooms() {
  const { t, lang } = useLang();
  const [active, setActive] = useState(null);
  const [idx, setIdx] = useState(0);

  const open = (room, i) => { setActive(room); setIdx(i); };
  const close = () => setActive(null);
  const prev = () => setIdx((p) => (p - 1 + active.images.length) % active.images.length);
  const next = () => setIdx((p) => (p + 1) % active.images.length);

  return (
    <section id="camere" className="py-24 md:py-36 bg-[#EFE5D4]">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="text-center max-w-2xl mx-auto mb-20">
          <p className="font-body text-[#B5603A] tracking-[0.35em] uppercase text-xs mb-5">{t('rooms.kicker')}</p>
          <h2 className="font-display text-4xl md:text-6xl text-[#2A2420] mb-5">{t('rooms.title')}</h2>
          <p className="font-body text-[#6E655B] text-lg leading-relaxed">{t('rooms.subtitle')}</p>
        </div>

        <div className="space-y-24 md:space-y-32">
          {rooms.map((room, i) => (
            <RoomCard key={room.id} room={room} index={i} onOpen={open} />
          ))}
        </div>
      </div>

      {/* Lightbox */}
      <Dialog open={!!active} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-4xl w-[95vw] p-0 bg-[#2A2420] border-none overflow-hidden">
          {active && (
            <div className="relative">
              <img src={active.images[idx]} alt={active.name[lang]} className="w-full max-h-[75vh] object-contain bg-black" />
              <button onClick={close} className="absolute top-4 right-4 text-white/80 hover:text-white bg-black/40 rounded-full p-2 transition-colors">
                <X className="w-5 h-5" />
              </button>
              <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/80 hover:text-white bg-black/40 rounded-full p-2.5 transition-colors">
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/80 hover:text-white bg-black/40 rounded-full p-2.5 transition-colors">
                <ChevronRight className="w-6 h-6" />
              </button>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-6 py-5">
                <p className="font-display text-2xl text-white">{active.name[lang]}</p>
                <p className="font-body text-xs text-white/70 tracking-wide">{idx + 1} / {active.images.length}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
